import { useState } from 'react';
import JxButton from '../../../JxControls/JxButtons/JxButton';
import JxToggle from '../../../JxControls/JxToggles/JxToggle.jsx';
import { JxSelect } from '@/JxControls';
import ImportFileDropzone from './ImportFileDropzone';
import ImportFileRow from './ImportFileRow';
import useFileImportGenerate from './useFileImportGenerate';
import useSeqDiaGenerate from './useSeqDiaGenerate';
import SeqDiaImportPanel from './SeqDiaImportPanel';
import { toFileEntries, summarizeStatus } from './UmlSchemaFileImportHelpers';

export const DIAGRAM_MODES = { CLASS: 'class', SEQUENCE: 'sequence' };
const DIAGRAM_MODE_OPTIONS = [
    { label: 'Class diagram', value: DIAGRAM_MODES.CLASS },
    { label: 'Sequence diagram', value: DIAGRAM_MODES.SEQUENCE },
];

/**
 * UmlSchemaFileImportTab — drop or select multiple source files at once,
 * grouped by extension (ImportStrategyRegistry.extensions). Generate reads
 * every PENDING file's text (client-side, File.text() — no upload) and runs
 * it through its matched strategy ONE FILE AT A TIME, in order, so file 2
 * sees file 1's shapes/connectors as "existing" (cross-file inheritance and
 * dependency resolution, and package re-use, come for free from that).
 *
 * droppedFiles/diagramMode are lifted to the PARENT dialog (UmlSchemaConfigDlg's
 * own this.state) rather than local useState — this tab unmounts on minimize
 * (see UmlSchemaConfigDlg.render: `if (... this.isMinimized) return null`,
 * which unmounts everything rendered inside), so anything kept in a local
 * hook here is lost on restore. The parent's class instance survives
 * minimize, so state living there does too. setDroppedFiles/setDiagramMode
 * behave like useState setters (accept a value OR an updater function) so
 * the rest of this file's existing functional updates need no changes.
 * @param {{ onSave: () => void, isSaving: boolean, name: string, droppedFiles: object[], setDroppedFiles: (updater: any) => void, diagramMode: string, setDiagramMode: (updater: any) => void }} props
 * @returns {JSX.Element}
 */
export default function UmlSchemaFileImportTab({ onSave, isSaving, name, droppedFiles, setDroppedFiles, diagramMode, setDiagramMode }) {
    const [lastWarnings, setLastWarnings] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [collapsed, setCollapsed] = useState(true);

    const { handleGenerate } = useFileImportGenerate({ droppedFiles, setDroppedFiles, setLastWarnings, setIsGenerating, collapsed });
    const seqDia = useSeqDiaGenerate({ droppedFiles });

    const isSequenceMode = diagramMode === DIAGRAM_MODES.SEQUENCE;

    const handleFilesAdded = (fileList, isFolder) => {
        setDroppedFiles(function appendEntries(current) {
            return [...current, ...toFileEntries(fileList)];
        });
        if (isFolder) {
            setDiagramMode(DIAGRAM_MODES.SEQUENCE); // a folder drop almost always means "trace a call chain across these files", not a single class-diagram import
        }
    };

    const handleRemoveFile = (id) => {
        setDroppedFiles(function removeEntry(current) {
            return current.filter(function keepOthers(entry) {
                return entry.id !== id;
            });
        });
    };

    const handleClearFiles = () => {
        setDroppedFiles([]);
        setLastWarnings([]);
    };

    const handleResetParsed = () => {
        setDroppedFiles(function resetEntries(current) {
            return current.map(function resetEntry(entry) {
                return entry.status === 'parsed' ? { ...entry, status: 'pending' } : entry;
            });
        });
        setLastWarnings([]);
    };

    const pendingCount = droppedFiles.filter(function isPending(entry) {
        return entry.status === 'pending';
    }).length;
    const parsedCount = droppedFiles.filter(function isParsed(entry) {
        return entry.status === 'parsed';
    }).length;

    return (
        <div className="usd-import">
            <div className="usd-button-row">
                <label htmlFor="usd-diagram-mode-select" className="usd-diagram-mode-label">Diagram:</label>
                <JxSelect
                    id="usd-diagram-mode-select"
                    value={diagramMode}
                    onChange={setDiagramMode}
                    options={DIAGRAM_MODE_OPTIONS}
                    defaultLabel="Class diagram"
                />
            </div>

            <div className="usd-import-text">Drop files — grouped by extension, parsed files are grayed out.</div>

            {droppedFiles.length > 0 ? (
                <div className="usd-import-file-list">
                    {droppedFiles.map(function renderFileRow(entry) {
                        return <ImportFileRow key={entry.id} entry={entry} onRemove={handleRemoveFile} />;
                    })}
                </div>
            ) : (
                <div className="usd-import-file-list usd-import-file-list-empty">No files added yet</div>
            )}

            <ImportFileDropzone onFilesAdded={handleFilesAdded} />

            {!isSequenceMode && lastWarnings.length > 0 && <div className="usd-import-text">Skipped: {lastWarnings.join('; ')}</div>}

            {!isSequenceMode && (
                <div className="usd-button-row">
                    <JxButton className="jx-button-sm" label="Save" onClick={onSave} disabled={!name?.trim() || isSaving} />
                    <div className="usd-button-spacer" />
                    <JxToggle id="uml-file-import-collapsed-toggle" checked={collapsed} onChange={setCollapsed} label="Collapsed" />
                    <JxButton
                        className="jx-button-sm"
                        label="Clear Files"
                        onClick={handleClearFiles}
                        disabled={droppedFiles.length === 0}
                    />
                    <JxButton className="jx-button-sm" label="Reset" onClick={handleResetParsed} disabled={parsedCount === 0} />
                    <JxButton
                        className="jx-button-sm"
                        label="Generate"
                        onClick={handleGenerate}
                        disabled={pendingCount === 0 || isGenerating}
                    />
                </div>
            )}

            {isSequenceMode && (
                <SeqDiaImportPanel seqDia={seqDia} droppedFilesLength={droppedFiles.length} onClearFiles={handleClearFiles} />
            )}

            <div className="usd-status-bar">
                <span>{summarizeStatus(droppedFiles)}</span>
            </div>
        </div>
    );
}
